import React, { useState } from "react";

import { UserAddIcon, UserRemoveIcon } from "@heroicons/react/outline";

import Card from "../Shared/Card/Card";
import Tooltip from "../Shared/Tooltips";
import LoadingWheel from "../Shared/LoadingWheel";
import ConfirmDelete from "../Shared/ConfirmDelete";
import { IMarketingGroup } from "../../models/marketingGroup/MarketingGroup";
import { useMarketingGroups } from "../../data/marketingGroup/useMarketingGroups";
import { IMarketingGroupMember } from './../../models/marketingGroupMember/MarketingGroupMember';
import { useMarketingGroupMutations } from "../../data/marketingGroup/useMarketingGroupMutations";

interface MarketingGroupRowProps extends IMarketingGroup {
  isMember: boolean;
  onAdd: () => void;
  onRemove: () => void;
}

function MarketingGroupRow({ name, isMember, onAdd, onRemove }: MarketingGroupRowProps) {

  return (
    <div className="grid grid-cols-10 py-2 px-4">
      <div className="col-span-8">
        {name || "Unknown Group"}
      </div>
      <div className="col-span-1">
        {isMember && (
          <span className="text-green-800 bg-green-100 rounded-full text-xs font-medium px-3 py-0.5">Member</span>
        )}
      </div>
      <div className="col-span-1 flex justify-end">
        {!isMember ?
          (
            <Tooltip tip="Add to group">
              <UserAddIcon
                className="w-5 h-5 text-green-500 cursor-pointer"
                onClick={() => onAdd()}
              />
            </Tooltip>
          ) : (
            <Tooltip tip="Remove from group">
              <UserRemoveIcon
                className="w-5 h-5 text-red-500 cursor-pointer"
                onClick={() => onRemove()}
              />
            </Tooltip>
          )}
      </div>
    </div>
  );

}

interface MarketingGroupMembershipProps {
  personId: string;
  marketingGroups: IMarketingGroupMember[];
}

export function MarketingGroupMembership({ personId, marketingGroups }: MarketingGroupMembershipProps) {
  const { marketingGroups: allMarketingGroups } = useMarketingGroups(true);
  const { removePeople, addPeople } = useMarketingGroupMutations();
  const [removeMarketingGroup, setRemoveMarketingGroup] = useState<string>();
  const [confirmRemoveMarketingGroupOpen, setConfirmRemoveMarketingGroupOpen] = useState(false);

  const handleRemoveMarketingGroup = (groupId: string) => {
    setRemoveMarketingGroup(groupId);
    setConfirmRemoveMarketingGroupOpen(true);
  }

  const handleConfirmRemoveMarketingGroup = () => {
    removePeople.mutate({
      marketingGroupId: removeMarketingGroup!,
      data: {
        people: [personId]
      }
    },
      {
        onSuccess: () => setConfirmRemoveMarketingGroupOpen(false)
      });
  }

  const handleAddMarketingGroup = (groupId: string) =>
    addPeople.mutate({
      marketingGroupId: groupId,
      data: {
        people: [personId]
      }
    });


  return (
    <>
      {/* @ts-ignore */}
      <ConfirmDelete
        itemName="Marketing Group Member"
        open={confirmRemoveMarketingGroupOpen}
        setOpen={setConfirmRemoveMarketingGroupOpen}
        onConfirm={handleConfirmRemoveMarketingGroup}
      />
      {/* @ts-ignore */}
      <Card title="Marketing Groups">
        {allMarketingGroups.isLoading ? (
          <div className="py-8">
            <LoadingWheel width="w-8" height="h-8" />
          </div>
        ) : (
          <>
            {allMarketingGroups.data?.data.length! > 0 ? (
              <div className="px-2 divide-y py-2">
                {allMarketingGroups.data?.data.map((group) => (
                  <MarketingGroupRow
                    key={group.id}
                    isMember={marketingGroups.some(mg => mg.marketingGroupId === group.id && !mg.unsubscribed)}
                    onAdd={() => handleAddMarketingGroup(group.id)}
                    onRemove={() => handleRemoveMarketingGroup(group.id)}
                    {...group}
                  />
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-sm italic text-gray-500">No marketing groups assigned.</div>
            )}
          </>
        )}
      </Card>
    </>
  )
}